import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { User, FileText, MessageSquare, Link2, Pencil, Trash2, Building2 } from "lucide-react";
import { useLocation } from "wouter";
import { formatDateTime, formatNoteDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/trackEvent";
import { EditNoteModal } from "@/components/insured/notes/EditNoteModal";
import { DeleteNoteConfirmModal } from "@/components/insured/notes/DeleteNoteConfirmModal";
import { NotePinButton } from "@/components/insured/notes/NotePinButton";
import type {
  TimelineEvent as TimelineEventModel,
  TimelineKind,
} from "./timeline-events";

// CSR-771 — Notes timeline card.
// Renders the orange `NOTES` badge, optional clickable `Policy POL-XXXXX`
// badge (insured timeline only — the policy tab already scopes to one policy),
// optional green `NowCerts` source badge, the title, the truncated-at-500-char
// details preview with `Show more` / `Show less`, the resolved author display
// name, the `MMM D, h:mm A` local-tz timestamp, the `(edited)` suffix, and
// the AMS-only Edit/Delete action icons.

const NOTE_PREVIEW_CHARS = 500;

const KIND_ICON: Record<TimelineKind, typeof FileText> = {
  notes: MessageSquare,
  comms: Link2,
  policy: User,
  // CSR-1472 — insured profile edits get a building glyph to read as
  // company-level (vs the person glyph used for policy events).
  insured: Building2,
  docs: FileText,
};

// Spec calls for orange (not yellow) on notes. Other kinds unchanged.
const KIND_BADGE_CLASS: Record<TimelineKind, string> = {
  notes: "bg-orange-50 text-orange-700 border-orange-200",
  comms: "bg-amber-50 text-amber-700 border-amber-200",
  policy: "bg-purple-50 text-purple-700 border-purple-200",
  // CSR-1472 — blue keeps Insured visually distinct from the purple Policy badge.
  insured: "bg-blue-50 text-blue-700 border-blue-200",
  docs: "bg-teal-50 text-teal-700 border-teal-200",
};

const KIND_TILE_CLASS: Record<TimelineKind, string> = {
  notes: "bg-orange-100 text-orange-700",
  comms: "bg-amber-100 text-amber-700",
  policy: "bg-purple-100 text-purple-700",
  insured: "bg-blue-100 text-blue-700",
  docs: "bg-teal-100 text-teal-700",
};

const KIND_LABEL: Record<TimelineKind, string> = {
  notes: "Notes",
  comms: "Comms",
  policy: "Policy",
  insured: "Insured",
  docs: "Docs",
};

// CSR-1472 — collapse insured field-change lists past this many rows, matching
// the policy activities card so a large edit doesn't dominate the timeline.
const COLLAPSED_CHANGE_LINES = 3;

interface Props {
  event: TimelineEventModel;
}

export function TimelineEventCard({ event }: Props) {
  const [, navigate] = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isNote = event.kind === "notes";
  const noteMeta = event.noteMeta;
  const isAms = isNote && noteMeta?.source === "ams";

  // CSR-1472 — insured edit events carry a per-field change list rendered like
  // the policy "Coverages updated" card, collapsed past COLLAPSED_CHANGE_LINES.
  const changes = event.changes ?? [];
  const hasHiddenChanges = changes.length > COLLAPSED_CHANGE_LINES;
  const visibleChanges =
    changesExpanded || !hasHiddenChanges
      ? changes
      : changes.slice(0, COLLAPSED_CHANGE_LINES);

  const fullBody = event.body ?? "";
  const truncated = fullBody.length > NOTE_PREVIEW_CHARS;
  const visibleBody = !truncated || expanded
    ? fullBody
    : `${fullBody.slice(0, NOTE_PREVIEW_CHARS)}…`;

  const onPolicyBadgeClick = () => {
    if (!noteMeta?.policyId || !noteMeta.insuredId) return;
    // Q-2: navigate to the inline policy detail under the insured.
    navigate(
      `/insureds/${noteMeta.insuredId}?tab=policies&policyId=${noteMeta.policyId}`,
    );
  };

  const Icon = KIND_ICON[event.kind];

  return (
    <div
      className="rounded-xl border border-border/40 bg-card p-4 hover:bg-muted/30 transition-colors relative"
      data-testid={`timeline-event-${event.id}`}
    >
      {/* Orange status dot for note rows. */}
      {isNote && (
        <span
          className="absolute -left-[27px] top-5 w-3 h-3 rounded-full bg-orange-500 ring-4 ring-card"
          aria-hidden="true"
        />
      )}
      <div className="flex gap-3">
        <div
          className={cn(
            "shrink-0 w-10 h-10 rounded-lg flex items-center justify-center",
            KIND_TILE_CLASS[event.kind],
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className={cn(
                "uppercase text-[10px] font-bold tracking-wider gap-1",
                KIND_BADGE_CLASS[event.kind],
              )}
            >
              {KIND_LABEL[event.kind]}
            </Badge>
            {isNote && noteMeta?.policyNumber && (
              <button
                type="button"
                onClick={onPolicyBadgeClick}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-foreground hover:underline"
                data-testid={`timeline-policy-badge-${noteMeta.noteId}`}
              >
                Policy {noteMeta.policyNumber}
              </button>
            )}
            {isNote && noteMeta?.source === "nowcerts" && (
              <Badge
                variant="outline"
                className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                data-testid={`timeline-nowcerts-badge-${noteMeta.noteId}`}
              >
                NowCerts
              </Badge>
            )}
            {!isNote && event.sourceLabel && (
              <Badge variant="outline" className="text-[10px]">
                {event.sourceLabel}
              </Badge>
            )}
            {/* Policy tag: clickable when the event carries policyLink
                (record-update cards, CSR-1960); a plain span otherwise. */}
            {!isNote && event.policyNumber && event.policyLink && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/insureds/${event.policyLink!.insuredId}?tab=policies&policyId=${event.policyLink!.policyId}`,
                  )
                }
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-foreground hover:underline cursor-pointer"
                data-testid={`timeline-policy-badge-${event.id}`}
              >
                Policy {event.policyNumber}
              </button>
            )}
            {!isNote && event.policyNumber && !event.policyLink && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-foreground"
                data-testid={`timeline-policy-tag-${event.id}`}
              >
                Policy {event.policyNumber}
              </span>
            )}
            {!isNote && event.sourceTag && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground"
                data-testid={`timeline-source-tag-${event.id}`}
              >
                {event.sourceTag}
              </span>
            )}
            {isNote && noteMeta && (
              <div className="ml-auto flex items-center gap-1">
                {/* CSR-1885 — pin is available on every note card (AMS + NowCerts). */}
                <NotePinButton
                  noteId={noteMeta.noteId}
                  pinned={noteMeta.pinned}
                  testId={`timeline-note-pin-${noteMeta.noteId}`}
                />
                {isAms && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditOpen(true)}
                      className="p-1 hover:bg-muted rounded cursor-pointer"
                      aria-label="Edit note"
                      data-testid={`timeline-note-edit-${noteMeta.noteId}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteOpen(true)}
                      className="p-1 hover:bg-muted rounded cursor-pointer"
                      aria-label="Delete note"
                      data-testid={`timeline-note-delete-${noteMeta.noteId}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <p className="text-sm font-semibold">{event.title}</p>
          {changes.length > 0 && (
            <div className="space-y-0.5" data-testid={`timeline-changes-${event.id}`}>
              {visibleChanges.map((line, index) => (
                <p
                  key={index}
                  className="text-sm text-muted-foreground whitespace-pre-wrap"
                >
                  {line}
                </p>
              ))}
              {hasHiddenChanges && (
                <button
                  type="button"
                  onClick={() => setChangesExpanded((prev) => !prev)}
                  className="text-xs text-primary hover:underline"
                  data-testid={`timeline-changes-toggle-${event.id}`}
                >
                  {changesExpanded
                    ? "Show less"
                    : `Show ${changes.length - COLLAPSED_CHANGE_LINES} more`}
                </button>
              )}
            </div>
          )}
          {fullBody && (
            <>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {visibleBody}
              </p>
              {truncated && (
                <button
                  type="button"
                  onClick={() => {
                    const next = !expanded;
                    setExpanded(next);
                    if (next && noteMeta) {
                      trackEvent("Note Expanded", {
                        note_id: noteMeta.noteId,
                        scope: noteMeta.policyId ? "policy" : "insured",
                      });
                    }
                  }}
                  className="text-xs text-primary hover:underline"
                  data-testid={`timeline-note-toggle-${noteMeta?.noteId ?? event.id}`}
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {event.authorName && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" aria-hidden="true" />
                {event.authorName}
              </span>
            )}
            <span>
              {event.formattedTimestamp ??
                (isNote
                  ? formatNoteDateTime(event.timestamp)
                  : formatDateTime(event.timestamp))}
            </span>
            {isNote && noteMeta?.isEdited && (
              <span data-testid={`timeline-note-edited-${noteMeta.noteId}`}>
                (edited)
              </span>
            )}
          </div>
        </div>
      </div>

      {isAms && noteMeta && (
        <>
          <EditNoteModal
            note={noteMeta.raw}
            open={editOpen}
            onOpenChange={setEditOpen}
          />
          <DeleteNoteConfirmModal
            note={noteMeta.raw}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
          />
        </>
      )}
    </div>
  );
}
